/**
 * UTM-driven product spotlighting on collection pages.
 *
 * Example:
 *   /collections/dresses?utm_products=julia-dress,cordelia-dress
 */

const PARAM = 'utm_products';
const MAX_PRODUCTS = 5;
const SECTION_ID = 'featured-product-card';
const FEATURED_CLASS = 'grid__item--featured-product';
const FILTER_UPDATE_EVENT = 'filter:update';
const PENDING_CLASS = 'featured-products-pending';

let applied = false;

function clearPendingState() {
  document.documentElement.classList.remove(PENDING_CLASS);
}

function getHandles() {
  const raw = new URLSearchParams(window.location.search).get(PARAM);
  if (!raw) return [];

  const seen = new Set();
  const handles = [];

  raw.split(',').forEach((part) => {
    const handle = part.trim().toLowerCase();
    if (!handle || seen.has(handle)) return;

    seen.add(handle);
    handles.push(handle);
  });

  return handles.slice(0, MAX_PRODUCTS);
}

function getRoot() {
  return window.Shopify?.routes?.root || '/';
}

function getItemHandle(item) {
  return item.dataset.productHandle || item.querySelector('[data-product-handle]')?.dataset.productHandle || '';
}

function findGrid() {
  return document.querySelector('#product-grid.product-grid');
}

function findItemByHandle(grid, handle) {
  return Array.from(grid.querySelectorAll(':scope > .grid__item')).find((item) => getItemHandle(item) === handle) || null;
}

function bindFocusState(item) {
  item.querySelectorAll('.card').forEach((card) => {
    if (card.dataset.featuredProductsFocusBound === 'true') return;

    card.dataset.featuredProductsFocusBound = 'true';
    card.addEventListener('focusin', () => card.classList.add('card--focused'));
    card.addEventListener('focusout', () => card.classList.remove('card--focused'));
  });
}

function markFeatured(item, handle) {
  item.classList.add(FEATURED_CLASS);
  item.dataset.featuredProduct = 'true';
  item.dataset.productHandle = handle;
  bindFocusState(item);
}

async function fetchFeaturedItem(handle) {
  const url = `${getRoot()}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;

  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const html = await response.text();
    const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.card-wrapper');
    if (!card) return null;

    const item = document.createElement('li');
    item.className = `grid__item ${FEATURED_CLASS}`;
    item.dataset.featuredProduct = 'true';
    item.dataset.productHandle = handle;

    const productId = card.querySelector('.card__main')?.dataset.productId;
    if (productId) item.dataset.productId = productId;

    item.appendChild(card);
    bindFocusState(item);

    return item;
  } catch (error) {
    return null;
  }
}

function refreshReviewWidgets() {
  if (!document.querySelector('.yotpo-widget-instance')) return;

  if (typeof window.yotpo?.refreshWidgets === 'function') {
    window.yotpo.refreshWidgets();
  } else if (typeof window.yotpo?.initWidgets === 'function') {
    window.yotpo.initWidgets();
  }
}

function removeDuplicateItems(grid, handles) {
  handles.forEach((handle) => {
    const matches = Array.from(grid.querySelectorAll(':scope > .grid__item')).filter((item) => getItemHandle(item) === handle);
    matches.slice(1).forEach((item) => item.remove());
  });
}

function guardAgainstDuplicates(grid, handles) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (!node.matches('.grid__item')) return;
        if (node.dataset.featuredProduct === 'true') return;
        if (handles.has(getItemHandle(node))) node.remove();
      });
    });
  });

  observer.observe(grid, { childList: true });

  document.addEventListener(FILTER_UPDATE_EVENT, () => observer.disconnect(), { once: true });
}

async function apply() {
  if (applied) return;
  if (document.documentElement.classList.contains('plp-restore-pending')) {
    clearPendingState();
    return;
  }

  const handles = getHandles();
  if (!handles.length) {
    clearPendingState();
    return;
  }

  const grid = findGrid();
  if (!grid) {
    clearPendingState();
    return;
  }

  applied = true;

  const items = await Promise.all(
    handles.map((handle) => {
      const existing = findItemByHandle(grid, handle);
      if (!existing) return fetchFeaturedItem(handle);

      markFeatured(existing, handle);
      return existing;
    })
  );

  const fragment = document.createDocumentFragment();
  items.forEach((item) => {
    if (item) fragment.appendChild(item);
  });

  if (!fragment.childNodes.length) {
    clearPendingState();
    return;
  }

  grid.prepend(fragment);
  removeDuplicateItems(grid, handles);
  guardAgainstDuplicates(grid, new Set(handles));
  refreshReviewWidgets();
  clearPendingState();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', apply, { once: true });
} else {
  apply();
}
